import React from "react";
import styled from "styled-components";
import { IconSvg } from "monica-alexandria";
import { read_timeline_date_label } from "../../../utils/timeline/timeline_formatting";
import {
    TimelineSalesEvents,
    TimelineSalesHeader,
    TimelineSalesOutcome,
    TimelineSalesSeller,
    TimelineSalesSellerText,
    TimelineSalesSession,
    TimelineSalesTimestamp
} from "../salesSession/Timeline_Sales_Session";
import { Timeline_Sales_Session_Changes } from "../salesSession/Timeline_Sales_Session_Changes";
import { Timeline_Sales_Session_Transition } from "../salesSession/Timeline_Sales_Session_Transition";
import {
    read_meeting_session_events,
    read_meeting_session_outcome,
    read_meeting_session_seller
} from "./meeting_session_view";

const meetingOutcomeStyleLookup = {
    Paid: { color: "#56b9a9", background: "rgba(86, 185, 169, 0.12)" },
    Lost: { color: "#e05b67", background: "rgba(224, 91, 103, 0.12)" },
    "Seller changed": { color: "#9b7bd5", background: "rgba(155, 123, 213, 0.12)" }
};
const neutralOutcomeStyle = { color: "#6f7480", background: "rgba(111, 116, 128, 0.12)" };
const activeOutcomeStyle = { color: "var(--main)", background: "transparent" };

const TimelineMeetingRow = styled.div`
    display: grid;
    gap: 0.3rem;
    min-width: 0;
    padding: 0.8rem 1rem;
    font-family: "fontMedium", sans-serif;

    p,
    strong,
    span,
    time {
        color: ${p => p.theme.color};
        overflow-wrap: anywhere;
        white-space: pre-wrap;
    }
`;

const TimelineMeetingComment = styled(TimelineMeetingRow)`
    grid-template-columns: 2.8rem minmax(0, 1fr);
    gap: 0.8rem;
`;

const TimelineMeetingIcon = styled.div`
    display: grid;
    place-items: center;
    width: 2.8rem;
    height: 2.8rem;
    border-radius: 50%;
    color: ${p => p.theme.color};
    background: ${p => p.theme.low};

    svg {
        width: 1.4rem;
        height: 1.4rem;
        fill: currentColor;
    }
`;

const TimelineMeetingSchedule = styled.p`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;

    strong {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
    }

    svg {
        width: 1.4rem;
        height: 1.4rem;
        fill: currentColor;
    }
`;

const TimelineMeetingSellerChange = styled.p`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.35rem;

    span:first-child {
        color: var(--greyDark);
    }

    span:nth-child(2) {
        color: ${p => p.theme.mid};
    }
`;

const Meeting_Event_Row = ({ event, t }) => {
    if (event.scenario === "seller_change") {
        return (
            <TimelineMeetingRow data-testid="timeline-meeting-session-seller-change">
                <TimelineMeetingSellerChange>
                    <span>{event.previousSeller}</span>
                    <span aria-hidden="true">→</span>
                    <strong>{event.nextSeller}</strong>
                </TimelineMeetingSellerChange>
                <p>{t("Changed by")} {event.actor}</p>
            </TimelineMeetingRow>
        );
    }

    if (event.scenario === "comment") {
        return (
            <TimelineMeetingComment data-testid="timeline-meeting-session-comment">
                <TimelineMeetingIcon title={t("comment")}><IconSvg Icon="FbComment" /></TimelineMeetingIcon>
                <div>
                    <p>{event.comment}</p>
                    <Timeline_Sales_Session_Changes event={event} t={t} />
                </div>
            </TimelineMeetingComment>
        );
    }

    if (event.scenario === "callback") {
        return (
            <TimelineMeetingRow data-testid="timeline-meeting-session-calendar">
                <Timeline_Sales_Session_Transition statusChange={event.statusChange} />
                <TimelineMeetingSchedule>
                    <strong title={t("calendar")}><IconSvg Icon="Calendar" /> {t("Meeting")}</strong>
                    <time dateTime={event.schedule}>{read_timeline_date_label(event.schedule)}</time>
                </TimelineMeetingSchedule>
                {event.comment && <p>{event.comment}</p>}
                <Timeline_Sales_Session_Changes event={event} t={t} />
            </TimelineMeetingRow>
        );
    }

    return (
        <TimelineMeetingRow data-testid={`timeline-meeting-session-${event.scenario}`}>
            {event.scenario === "status" && <Timeline_Sales_Session_Transition statusChange={event.statusChange} />}
            <Timeline_Sales_Session_Changes event={event} t={t} />
        </TimelineMeetingRow>
    );
};

// Displays one active or completed Godfather seller tenure as a standalone Meeting-session card.
export default function Timeline_Meeting_Session(props) {
    const active = props.event?.data?.active === true;
    const seller = read_meeting_session_seller(props.event, props.teamMembers);
    const outcome = active ? "Active" : read_meeting_session_outcome(props.event);
    const outcomeStyle = active ? activeOutcomeStyle : meetingOutcomeStyleLookup[outcome] || neutralOutcomeStyle;
    const timestamp = active
        ? props.event?.data?.openedAt || props.event?.from?.openedAt
        : props.event?.data?.closedAt || props.event?.to?.closedAt;
    const events = read_meeting_session_events(props.event);

    return (
        <TimelineSalesSession data-testid="timeline-meeting-session" aria-label={props.t(active ? "Active Meeting session" : "Completed Meeting session")}>
            <TimelineSalesHeader>
                <TimelineSalesSeller>
                    <TimelineSalesSellerText><h4>{seller.name}</h4></TimelineSalesSellerText>
                    <TimelineSalesOutcome
                        $statusStyle={outcomeStyle}
                        data-testid="timeline-meeting-outcome"
                        title={`${props.t(active ? "Status" : "Outcome")}: ${props.t(outcome)}`}
                    >
                        <h6>{props.t(outcome)}</h6>
                    </TimelineSalesOutcome>
                </TimelineSalesSeller>
                <TimelineSalesTimestamp dateTime={timestamp}>{read_timeline_date_label(timestamp)}</TimelineSalesTimestamp>
            </TimelineSalesHeader>
            <TimelineSalesEvents>
                {events.map((event) => <Meeting_Event_Row key={event.key} event={event} t={props.t} />)}
            </TimelineSalesEvents>
        </TimelineSalesSession>
    );
}

export { Timeline_Meeting_Session };
